import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { Page } from "v2/components/Page";
import { StatusBadge } from "v2/components/StatusBadge";
import { IExecutionDetail } from "./contracts/types";
import { formatSaoPauloDateTime, formatUtcTooltip } from "./dates";
import { getWorkspaceTimeline } from "./operational-panel.service";

export function WorkspacePage() {
  const { workspaceId, activityId } = useParams<{ workspaceId: string; activityId: string }>();
  const history = useHistory();
  const [detailExecution, setDetailExecution] = useState<IExecutionDetail | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["operational-panel", "workspace", workspaceId, activityId],
    queryFn: () => getWorkspaceTimeline(workspaceId, activityId),
    refetchInterval: 5000,
  });

  return (
    <>
    <Page title={data ? data.activityName : "Atividade"}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <IconButton size="small" onClick={() => history.goBack()}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h6">{data?.buyerName ?? "—"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {data?.operatorName} · {data?.productName}
            </Typography>
          </Box>
        </Stack>

        {isLoading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {isError && <Alert severity="warning">Não foi possível confirmar o estado atual. Dado pode estar desatualizado.</Alert>}

        {!isLoading && !isError && !data && <Alert severity="info">Nenhuma execução encontrada na janela observada.</Alert>}

        {data && (
          <>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              {data.activityName}
            </Typography>
            {data.recentFailureRate && (
              <Typography variant="body2" color="error.main">
                {data.recentFailureRate.failures} falhas nas últimas {data.recentFailureRate.total} execuções
              </Typography>
            )}
            {data.lastSuccessAt && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Último sucesso: {formatSaoPauloDateTime(data.lastSuccessAt)}
                {data.daysSinceLastSuccess !== undefined ? ` · há ${data.daysSinceLastSuccess} dias` : ""}
              </Typography>
            )}

            <List sx={{ mt: 1 }}>
              {data.executions.map((execution) => (
                <ListItem key={execution.id} sx={{ borderBottom: "1px solid", borderColor: "divider", px: 0 }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
                    <StatusBadge status={execution.status} label=" " />
                    <Box sx={{ minWidth: 130 }}>
                      <Typography variant="body2" title={formatUtcTooltip(execution.executedAt)}>
                        {formatSaoPauloDateTime(execution.executedAt)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                      {execution.origin === "airflow" ? "Airflow" : "App"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ fontFamily: "monospace", flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {execution.dagRunId}
                    </Typography>
                    <Button size="small" onClick={() => setDetailExecution(execution)} sx={{ flexShrink: 0 }}>
                      Detalhe
                    </Button>
                  </Stack>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Box>
    </Page>

      <Dialog open={!!detailExecution} onClose={() => setDetailExecution(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhe da execução</DialogTitle>
        <DialogContent>
          {detailExecution && (
            <Stack spacing={1.5} sx={{ pb: 1 }}>
              <Typography variant="body2">
                <strong>dag_run_id:</strong> {detailExecution.dagRunId}
              </Typography>
              <Typography variant="body2">
                <strong>Origem:</strong> {detailExecution.origin === "airflow" ? "Airflow" : "App"}
              </Typography>

              {detailExecution.qbem ? (
                <>
                  <Typography variant="body2">
                    <strong>id_arquivo / id_carga:</strong> {detailExecution.qbem.idArquivo} / {detailExecution.qbem.idCarga}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Registros:</strong> {detailExecution.qbem.totalRecords} total ·{" "}
                    {detailExecution.qbem.validRecords} válidos · {detailExecution.qbem.inconsistentRecords} inconsistentes
                  </Typography>
                  {/*
                    T4 (2026-08-11): uma retenção não é mais, por si só, uma
                    falha — o veredito vem de `critical_errors` e
                    `inconsistent_records`, nunca do `status` isolado (spec
                    §6.1). Daí a severidade seguir o veredito da execução em vez
                    de ser sempre "error": dizer "falhou por retenção" numa
                    carga que resolveu SUCCESS seria a mesma contradição do D6.
                  */}
                  {detailExecution.qbem.reason === "RETENTIONS" && detailExecution.qbem.retainedCount !== undefined && (
                    <Alert severity={detailExecution.status === "FAILED" ? "error" : "info"}>
                      Confirmada com retenção — {detailExecution.qbem.retainedCount} registro
                      {detailExecution.qbem.retainedCount > 1 ? "s" : ""} retido
                      {detailExecution.qbem.retainedCount > 1 ? "s" : ""}
                    </Alert>
                  )}
                  {detailExecution.qbem.criticalErrors && detailExecution.qbem.criticalErrors.length > 0 && (
                    <Alert severity="error">{JSON.stringify(detailExecution.qbem.criticalErrors)}</Alert>
                  )}
                  {detailExecution.qbem.errorMessage && (
                    <Alert severity="error">{detailExecution.qbem.errorMessage}</Alert>
                  )}
                  {/*
                    T4(b): o artefato que fecha o ciclo entre "o painel acusou"
                    e "dá para resolver". A coluna sempre existiu
                    (`inconsistency_file_key`, preenchida em 17 das 119
                    execuções em produção); o painel é que não a lia.
                  */}
                  {detailExecution.qbem.inconsistencyFileKey && (
                    <Typography variant="body2">
                      <strong>Relatório de inconsistências:</strong>{" "}
                      <Box component="code" sx={{ wordBreak: "break-all" }}>
                        {detailExecution.qbem.inconsistencyFileKey}
                      </Box>
                    </Typography>
                  )}
                </>
              ) : detailExecution.errorMessage ? (
                <Alert severity="error">{detailExecution.errorMessage}</Alert>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Mensagem de erro não disponível para este fluxo — depende do adapter de leitura do Airflow (Fase D
                  do plano de backend).
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary">
                Busque <strong>{detailExecution.dagRunId}</strong> no Airflow para o log completo da task. Link
                direto depende do adapter de leitura do Airflow (Fase D do plano de backend).
              </Typography>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
